// lib/services/capture/color_camera_pool.ts

import TypedSlotPool, { PostReleasePolicy } from "../compute/typed_slot_pool";
import ColorCameraSlot from "./color_camera_slot";
import { YuvFormat } from "../compute/yuvdefs"

const SLOT_COUNT = 3;

/**
 * Three-slot pool of per-frame GPU camera textures.
 *
 * Producer: CaptureService
 *   - acquires an Idle slot
 *   - writeTextures the Y/UV bytes into the slot's pre-allocated textures
 *   - publishes the slot (assigns sequence + timestamp, recycles older Ready
 *     slots that are not currently leased)
 *
 * Consumer: ComputeService → StickerCompute
 *   - leases the latest Ready slot
 *   - binds the slot's texture views into its compute pipelines
 *   - releases when the compute pass has been submitted
 *
 * The pool uses PostReleasePolicy.KeepReady so the compute pipeline can
 * re-process the same frame if a new one hasn't arrived yet (mirrors the
 * animation pool's render-side re-lease pattern).
 *
 * Texture sizes are fixed at initialize time. The camera's chosen
 * resolution and format are known from CameraInfo before init, so we
 * never need to reallocate mid-stream.
 */
export default class ColorCameraPool {
  private pool: TypedSlotPool<ColorCameraSlot>;
  private slots: ColorCameraSlot[] = [];
  private initialized: boolean = false;
  private sequence_counter: number = 0;

  private y_width: number = 0;
  private y_height: number = 0;
  private chroma_width: number = 0;
  private chroma_height: number = 0;
  private format: YuvFormat = "NV12";

  constructor() {
    this.pool = new TypedSlotPool<ColorCameraSlot>(PostReleasePolicy.KeepReady);
  }

  /**
   * Allocate the pool's slots and their GPU textures. The camera's
   * resolution and format are known up front from CameraInfo.
   *
   * For NV12: chroma_width = y_width / 2, chroma_height = y_height / 2
   *           (single rg8 texel per 2x2 luma block).
   * For I420: chroma_width = y_width / 2, chroma_height = y_height / 2
   *           (separate u and v planes).
   */
  initialize(
    device: GPUDevice,
    key: string,
    format: YuvFormat,
    y_width: number,
    y_height: number,
    chroma_width: number,
    chroma_height: number
  ): boolean {
    if (this.initialized) return true;
    if (y_width <= 0 || y_height <= 0) {
      throw new Error(
        `ColorCameraPool.initialize: y_width (${y_width}) and y_height (${y_height}) must be positive.`
      );
    }

    this.format = format;
    this.y_width = y_width;
    this.y_height = y_height;
    this.chroma_width = chroma_width;
    this.chroma_height = chroma_height;

    this.slots = [];
    for (let i = 0; i < SLOT_COUNT; i++) {
      const slot = new ColorCameraSlot();
      slot.allocate_textures(
        device,
        key,
        i,
        format,
        y_width,
        y_height,
        chroma_width,
        chroma_height
      );
      this.slots.push(slot);
    }

    this.pool.initialize(this.slots);
    this.initialized = true;
    return true;
  }


  clear():void
  {
    for (const slot of this.slots) 
     {
         this.pool.mark_slot_never_used(slot);
     }
  }
  

  shutdown(): void {
    for (const slot of this.slots) {
      slot.destroy_textures();
    }
    this.pool.shutdown();
    this.slots = [];
    this.y_width = 0;
    this.y_height = 0;
    this.chroma_width = 0;
    this.chroma_height = 0;
    this.sequence_counter = 0;
    this.initialized = false;
  }

  is_initialized(): boolean {
    return this.initialized;
  }

  // ---- Producer-side API --------------------------------------------------

  /**
   * Acquire an Idle slot to fill with a new camera frame. The caller is
   * responsible for transitioning the slot through begin_record → write
   * texture bytes → publish_slot (or cancel_slot on failure).
   *
   * Returns null if no slot is currently Idle (all are Recording, Ready, or
   * Leased). In normal triple-buffer operation this should be rare; if it
   * happens the capture service can drop the frame.
   */
  acquire_idle_slot(): ColorCameraSlot | null {
    return this.pool.acquire_idle_slot();
  }

  /**
   * Helper: assign the next monotonic sequence + timestamp, mark the slot
   * Ready, and recycle any older Ready slots that are not currently leased.
   * Same shape as AnimationPool.publish_slot.
   */
  publish_slot(slot: ColorCameraSlot, timestamp_sec: number): void {
    this.sequence_counter += 1;
    this.pool.publish_slot(slot, this.sequence_counter, timestamp_sec);
  }

  /**
   * Cancel a Recording slot, returning it to Idle. Used when the frame
   * upload fails or is aborted.
   */
  cancel_slot(slot: ColorCameraSlot): void {
    slot.cancel_record();
    this.pool.cancel_slot(slot);
  }

  // ---- Consumer-side API --------------------------------------------------

  /**
   * Lease the most recent Ready slot. Returns null if no frame has been
   * published yet or if a lease is already held.
   *
   * The consumer must call release_latest_ready_slot when it is done with
   * the textures. Until then the slot will not be recycled.
   */
  acquire_latest_ready_slot(): ColorCameraSlot | null {
    return this.pool.acquire_latest_ready_slot();
  }

  /**
   * Release the currently leased slot. Under KeepReady policy the slot
   * stays Ready and may be re-leased if no newer slot has arrived.
   */
  release_latest_ready_slot(): void {
    this.pool.release_leased_slot();
  }

  // ---- Introspection ------------------------------------------------------

  get_format(): YuvFormat { return this.format; }
  get_y_width(): number { return this.y_width; }
  get_y_height(): number { return this.y_height; }
  get_chroma_width(): number { return this.chroma_width; }
  get_chroma_height(): number { return this.chroma_height; }

  get_slot_count(): number { return this.pool.getSlotCount(); }
  get_idle_count(): number { return this.pool.getIdleCount(); }
  get_latest_published_sequence(): number { return this.pool.getLatestPublishedSequence(); }
  get_latest_consumed_sequence(): number { return this.pool.getLatestConsumedSequence(); }
  get_superseded_count(): number { return this.pool.getSupersededCount(); }
  has_published_slot(): boolean { return this.pool.getLatestPublishedSequence() > 0; }

  // lib/services/capture/color_camera_pool.ts
// Add this method to the existing ColorCameraPool class.

/**
 * Read-only access to the latest Ready slot WITHOUT acquiring a lease.
 *
 * Safe to use within a single synchronous tick (e.g. binding texture views
 * to a video-display render pipeline and submitting in the same frame).
 * Does not participate in the lease chain — the slot can still be recycled
 * when a newer one is published.
 *
 * Use this for the video display pipeline. Use acquire_latest_ready_slot
 * for any consumer that runs across async boundaries.
 */
    peek_latest_ready_slot(): ColorCameraSlot | null {
    return this.pool.peek_latest_ready_slot();
    }
}